import { VALIDATION_RULES } from './constants';

/**
 * Form Validation Utilities
 */

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {string|null} Error message or null
 */
export const validateEmail = (email) => {
  if (!email) {
    return 'Email is required';
  }
  if (!VALIDATION_RULES.email.test(email)) {
    return 'Please enter a valid email address';
  }
  return null;
};

/**
 * Validate phone number (Uganda format)
 * @param {string} phone - Phone number to validate
 * @returns {string|null} Error message or null
 */
export const validatePhone = (phone) => {
  if (!phone) {
    return 'Phone number is required';
  }
  if (!VALIDATION_RULES.phone.test(phone)) {
    return 'Please enter a valid Ugandan phone number (e.g., +256XXXXXXXXX or 07XXXXXXXX)';
  }
  return null;
};

/**
 * Validate required field
 * @param {any} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} Error message or null
 */
export const validateRequired = (value, fieldName = 'This field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Validate minimum length
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum length
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} Error message or null
 */
export const validateMinLength = (value, minLength, fieldName = 'This field') => {
  if (!value) {
    return null; // Use validateRequired separately
  }
  if (value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }
  return null;
};

/**
 * Validate maximum length
 * @param {string} value - Value to validate
 * @param {number} maxLength - Maximum length
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} Error message or null
 */
export const validateMaxLength = (value, maxLength, fieldName = 'This field') => {
  if (!value) {
    return null; // Use validateRequired separately
  }
  if (value.length > maxLength) {
    return `${fieldName} must not exceed ${maxLength} characters`;
  }
  return null;
};

/**
 * Validate name (letters, spaces, hyphens only)
 * @param {string} name - Name to validate
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} Error message or null
 */
export const validateName = (name, fieldName = 'Name') => {
  if (!name) {
    return `${fieldName} is required`;
  }
  if (name.length < VALIDATION_RULES.minNameLength) {
    return `${fieldName} must be at least ${VALIDATION_RULES.minNameLength} characters`;
  }
  if (name.length > VALIDATION_RULES.maxNameLength) {
    return `${fieldName} must not exceed ${VALIDATION_RULES.maxNameLength} characters`;
  }
  if (!/^[a-zA-Z\s'-]+$/.test(name)) {
    return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
  }
  return null;
};

/**
 * Validate contact form
 * @param {object} values - Form values
 * @returns {object} Validation errors
 */
export const validateContactForm = (values) => {
  const errors = {};

  // Name validation
  const nameError = validateName(values.name, 'Name');
  if (nameError) errors.name = nameError;

  // Email validation
  const emailError = validateEmail(values.email);
  if (emailError) errors.email = emailError;

  // Phone validation
  const phoneError = validatePhone(values.phone);
  if (phoneError) errors.phone = phoneError;

  // Subject validation
  if (!values.subject || !values.subject.trim()) {
    errors.subject = 'Subject is required';
  } else if (values.subject.length < 3) {
    errors.subject = 'Subject must be at least 3 characters';
  } else if (values.subject.length > 200) {
    errors.subject = 'Subject must not exceed 200 characters';
  }

  // Message validation
  if (!values.message || !values.message.trim()) {
    errors.message = 'Message is required';
  } else if (values.message.length < VALIDATION_RULES.minMessageLength) {
    errors.message = `Message must be at least ${VALIDATION_RULES.minMessageLength} characters`;
  } else if (values.message.length > VALIDATION_RULES.maxMessageLength) {
    errors.message = `Message must not exceed ${VALIDATION_RULES.maxMessageLength} characters`;
  }

  return errors;
};

/**
 * Validate membership application form
 * @param {object} values - Form values
 * @returns {object} Validation errors
 */
export const validateMembershipForm = (values) => {
  const errors = {};

  // Full name validation
  const nameError = validateName(values.fullName, 'Full name');
  if (nameError) errors.fullName = nameError;

  // Email validation
  const emailError = validateEmail(values.email);
  if (emailError) errors.email = emailError;

  // Phone validation
  const phoneError = validatePhone(values.phone);
  if (phoneError) errors.phone = phoneError;

  // Organization name validation (if applicable)
  if (values.organizationType === 'organization') {
    if (!values.organizationName || !values.organizationName.trim()) {
      errors.organizationName = 'Organization name is required';
    } else if (values.organizationName.length < 3) {
      errors.organizationName = 'Organization name must be at least 3 characters';
    }
  }

  // Location validation
  if (!values.location || !values.location.trim()) {
    errors.location = 'Location is required';
  }

  // Type of member validation
  if (!values.memberType) {
    errors.memberType = 'Please select member type';
  }

  // Terms acceptance validation
  if (!values.acceptTerms) {
    errors.acceptTerms = 'You must accept the terms and conditions';
  }

  return errors;
};

/**
 * Validate quote request form
 * @param {object} values - Form values
 * @returns {object} Validation errors
 */
export const validateQuoteForm = (values) => {
  const errors = {};

  // Name validation
  const nameError = validateName(values.name, 'Name');
  if (nameError) errors.name = nameError;

  // Email validation
  const emailError = validateEmail(values.email);
  if (emailError) errors.email = emailError;

  // Phone validation
  const phoneError = validatePhone(values.phone);
  if (phoneError) errors.phone = phoneError;

  // Product validation
  if (!values.product) {
    errors.product = 'Please select a product';
  }

  // Quantity validation
  if (!values.quantity) {
    errors.quantity = 'Quantity is required';
  } else if (isNaN(values.quantity) || values.quantity <= 0) {
    errors.quantity = 'Please enter a valid quantity';
  }

  // Delivery location validation
  if (!values.deliveryLocation || !values.deliveryLocation.trim()) {
    errors.deliveryLocation = 'Delivery location is required';
  }

  return errors;
};

export default {
  validateEmail,
  validatePhone,
  validateRequired,
  validateMinLength,
  validateMaxLength,
  validateName,
  validateContactForm,
  validateMembershipForm,
  validateQuoteForm,
};